
import { Context } from '@nuxt/types'
import { defineComponent } from 'vue'
import MachinationsLightPage from '~/domain/pages/MachinationsLightPage'
import {
  DesignersStoreActions,
  DiagramStoreActions,
  DiagramTypes,
  UserType,
  Diagram,
} from '~/domain/interfaces'
import {
  HIGHLIGHTED_DIAGRAMS_COUNT,
  HIGHLIGHTED_DESIGNERS_COUNT,
  EXPLORE_DIAGRAMS_COUNT,
  EXPLORE_DESIGNERS_COUNT,
  FOR_HIRE_DESIGNERS_COUNT,
} from '~/domain/constants'
import CommunityCardGrid from '~/components/community/CommunityCardGrid.vue'
import CommonCommunity from '~/components/common/CommonCommunity.vue'
import CommonTextBlock from '~/components/common/CommonTextBlock.vue'
import CommonSearchBar from '~/components/common/CommonSearchBar.vue'

export default defineComponent({
  components: {
    CommonTextBlock,
    CommonCommunity,
    CommunityCardGrid,
    CommonSearchBar,
  },
  extends: MachinationsLightPage,
  layout: 'communityLayout',
  async asyncData(context: Context) {
    const { store } = context

    await store.dispatch(DiagramStoreActions.GET_DIAGRAMS, {
      context,
      params: {
        diagramType: DiagramTypes.POPULAR,
        diagramPage: 1,
        authenticatedUser: store.getters.userIsAuthenticated,
        count: EXPLORE_DIAGRAMS_COUNT + HIGHLIGHTED_DIAGRAMS_COUNT,
      },
    })
    await store.dispatch(DiagramStoreActions.GET_DIAGRAMS_OF_THE_MONTH, context)

    await store.dispatch('designers/fetchDesignersList', {
      context,
      page: 1,
      type: 'popular',
      authenticatedUser: store.getters.userIsAuthenticated,
      count: EXPLORE_DESIGNERS_COUNT + HIGHLIGHTED_DESIGNERS_COUNT,
      getLastSeen: true,
      allUsersCount: true,
    })
    await store.dispatch(DesignersStoreActions.FETCH_FEATURED, context)
    await store.dispatch('common/setupPageLight', { context })
  },
  data() {
    return {
      exploreLoading: true,
      initialTotalDiagrams: 0 as number | null | undefined,
      initialTotalDesigners: 0 as number | null | undefined,
      searchPlaceholder: this.$t('communitySearchPlaceholder') as string,
      searchFiltersItems: ['Diagrams', 'Designers'],
      columns: 1,
      block: {
        customBackground: false,
        text: '<h1>Be part of the biggest systems thinkers community on the planet</h1>\n<p>&nbsp;</p>\n<p>Create a Machinations account & become part of the biggest systems thinkers community on the planet. Connect with other designers, get inspired by their creations, and model your own diagrams. Build a shareable portfolio where your designs can be set in motion with a click of a button!</p>\n',
        googleCalendarLink: 'https://www.google.com/calendar',
        title:
          'Be part of the biggest systems thinkers community on the planet',
        backgroundColor: true,
        ctaButton: {
          isUsed: true,
          useSalesButton: false,
          dialogId: '',
          dialogType: 'Google',
          text: 'Sign-up for Machinations',
          link: 'https://my.machinations.io/register',
          outlined: false,
          enableTracking: false,
          trackingLocation: '',
        },
        componentStyle: {
          paddingTop: 60,
          paddingBottom: 90,
        },
      },
    }
  },
  async fetch(): Promise<void> {
    this.initialTotalDiagrams = this.getInitialTotal('diagrams')
    this.initialTotalDesigners = this.getInitialTotal('designers')
    this.exploreLoading = false

    await this.$store.dispatch(DesignersStoreActions.FETCH_FOR_HIRE, {
      context: this,
      count: FOR_HIRE_DESIGNERS_COUNT,
    })
  },
  head() {
    // @ts-ignore
    const pageHead = this.pageHead()
    return {
      ...pageHead,
      title: 'Join The Largest System Design Community',
      meta: [
        {
          name: 'Community',
          content:
            'Explore what other designers are crafting. Unlock new insights and connect with like-minded professionals.',
        },
        {
          hid: 'og:title',
          name: 'og:title',
          // @ts-ignore
          content: 'Join The Largest System Design Community',
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content:
            'Explore the Machinations Community of designers and creative minds and discover their diagrams.',
        },
        {
          hid: 'description',
          name: 'description',
          content:
            'Explore the Machinations Community of designers and creative minds and discover their diagrams.',
        },
        ...pageHead?.meta,
      ],
    }
  },
  fetchOnServer: false,
  computed: {
    totalDiagrams() {
      return this.$store.state.diagrams.total
    },
    exploreDiagrams() {
      const maxDiagrams = this.exploreLoading
        ? EXPLORE_DIAGRAMS_COUNT
        : this.columns * 2
      const diagrams = this.$store.state.diagrams.popular?.filter(
        (d: Diagram) => {
          return (
            this.$store.state.diagrams.highlighted?.filter(
              (h: Diagram) => h.fuid === d.fuid
            )?.length === 0
          )
        }
      )
      return this.$store.state.diagrams?.highlighted
        ?.map((diagram: Diagram) => ({ ...diagram, starred: true }))
        ?.concat(diagrams)
        ?.slice(0, maxDiagrams)
    },
    totalDesigners() {
      return this.$store.state.designers.total
    },
    exploreDesigners() {
      const maxDesigners = this.exploreLoading
        ? EXPLORE_DESIGNERS_COUNT
        : this.columns * 2
      const designers = this.$store.state.designers.designersList?.filter(
        (d: UserType) => {
          return (
            this.$store.state.designers.featured?.filter(
              (featured: UserType) => featured.username === d.username
            )?.length === 0
          )
        }
      )
      return this.$store.state.designers.featured
        ?.concat(designers)
        ?.slice(0, maxDesigners)
    },
    forHireDesigners() {
      return this.$store.state.designers.forHire.slice(0, this.columns)
    },
  },
  mounted() {
    setTimeout(() => {
      this.updateColumns()
    }, 500)
    window.addEventListener('resize', this.updateColumns)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateColumns)
  },
  methods: {
    seeAll(value: string) {
      this.$router.push(`/community/see-all?filter=${value}`)
    },
    getInitialTotal(type = 'diagrams') {
      if (document) {
        const t =
          document?.getElementById(`animated-total-${type}`)?.textContent || ''
        return t && typeof t !== 'undefined'
          ? +t
          : this.$store.state[type].total - 10
      }
      return this.$store.state[type].total - 10
    },
    updateColumns() {
      const grid = (this.$refs.grid as Vue)?.$el as HTMLElement

      if (grid) {
        const computedStyle = window.getComputedStyle(grid)
        const gridTemplateColumns = computedStyle.gridTemplateColumns.split(' ')
        this.columns = gridTemplateColumns.length // Get the number of columns
      }
    },
  },
})
